import { useState, useEffect } from "react";
import { supabase } from "@/lib/supabaseClient";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import {
  CheckCircle,
  XCircle,
  Clock,
  Building,
  Mail,
  Phone,
  FileText,
} from "lucide-react";

interface Agent {
  id: string;
  user_id: string;
  company_name: string;
  company_address?: string;
  license_number?: string;
  business_type?: string;
  status: string;
  created_at: string;
  user: {
    name: string;
    email: string;
    phone?: string;
  };
}

export default function AgentsApprovalPage() {
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const { toast } = useToast();

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const { data, error } = await supabase
        .from("agents")
        .select(
          `
          *,
          user:users(name, email, phone)
        `
        )
        .order("created_at", { ascending: false });

      if (error) throw error;
      setAgents(data || []);
    } catch (error) {
      console.error("Error fetching agents:", error);
      toast({
        title: "Error",
        description: "Failed to fetch agents",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApproval = async (
    agentId: string,
    status: "approved" | "rejected"
  ) => {
    setActionLoading(agentId);
    try {
      const { error } = await supabase
        .from("agents")
        .update({
          status,
          ...(status === "rejected" &&
            rejectionReason && { rejection_reason: rejectionReason }),
        })
        .eq("id", agentId);

      if (error) throw error;

      toast({
        title: "Success",
        description: `Agent ${status} successfully`,
      });

      fetchAgents();
      setRejectionReason("");
    } catch (error) {
      console.error("Error updating agent status:", error);
      toast({
        title: "Error",
        description: `Failed to ${status} agent`,
        variant: "destructive",
      });
    } finally {
      setActionLoading(null);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "approved":
        return (
          <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
            Approved
          </Badge>
        );
      case "rejected":
        return (
          <Badge className="bg-red-100 text-red-800 hover:bg-red-100">
            Rejected
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">
            Pending
          </Badge>
        );
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const filteredAgents = (status?: string) => {
    return status ? agents.filter((agent) => agent.status === status) : agents;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Agent Approvals</h1>
        <p className="text-gray-600 mt-1">
          Review and approve pending agent registrations
        </p>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="pending">
            Pending ({filteredAgents("pending").length})
          </TabsTrigger>
          <TabsTrigger value="approved">
            Approved ({filteredAgents("approved").length})
          </TabsTrigger>
          <TabsTrigger value="rejected">
            Rejected ({filteredAgents("rejected").length})
          </TabsTrigger>
          <TabsTrigger value="all">All ({agents.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {filteredAgents("pending").map((agent) => (
            <Card key={agent.id} className="border-yellow-200 bg-yellow-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">
                        {agent.company_name}
                      </CardTitle>
                      <p className="text-sm text-gray-600">{agent.user.name}</p>
                    </div>
                  </div>
                  {getStatusBadge(agent.status)}
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{agent.user.email}</span>
                  </div>
                  {agent.user.phone && (
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{agent.user.phone}</span>
                    </div>
                  )}
                  {agent.license_number && (
                    <div className="flex items-center space-x-2">
                      <FileText className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">
                        License: {agent.license_number}
                      </span>
                    </div>
                  )}
                  {agent.business_type && (
                    <div className="flex items-center space-x-2">
                      <Building className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">
                        Type: {agent.business_type}
                      </span>
                    </div>
                  )}
                </div>

                {agent.company_address && (
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">Address:</span>{" "}
                    {agent.company_address}
                  </p>
                )}

                <div className="flex items-center space-x-2 pt-4 border-t">
                  <Button
                    size="sm"
                    onClick={() => handleApproval(agent.id, "approved")}
                    disabled={actionLoading === agent.id}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Approve
                  </Button>

                  <Dialog>
                    <DialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        disabled={actionLoading === agent.id}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Reject
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Reject Agent</DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        <p className="text-sm text-gray-600">
                          Please provide a reason for rejecting this agent
                          registration:
                        </p>
                        <Textarea
                          placeholder="Enter rejection reason..."
                          value={rejectionReason}
                          onChange={(e) => setRejectionReason(e.target.value)}
                          rows={3}
                        />
                        <div className="flex space-x-2">
                          <Button
                            variant="destructive"
                            onClick={() => handleApproval(agent.id, "rejected")}
                            disabled={
                              !rejectionReason.trim() ||
                              actionLoading === agent.id
                            }
                          >
                            Confirm Rejection
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredAgents("pending").length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No pending agent approvals</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="approved" className="space-y-4">
          {filteredAgents("approved").map((agent) => (
            <Card key={agent.id} className="border-green-200 bg-green-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">
                        {agent.company_name}
                      </CardTitle>
                      <p className="text-sm text-gray-600">{agent.user.name}</p>
                    </div>
                  </div>
                  {getStatusBadge(agent.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{agent.user.email}</span>
                  </div>
                  {agent.user.phone && (
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{agent.user.phone}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredAgents("approved").length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <CheckCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No approved agents</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rejected" className="space-y-4">
          {filteredAgents("rejected").map((agent) => (
            <Card key={agent.id} className="border-red-200 bg-red-50">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">
                        {agent.company_name}
                      </CardTitle>
                      <p className="text-sm text-gray-600">{agent.user.name}</p>
                    </div>
                  </div>
                  {getStatusBadge(agent.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{agent.user.email}</span>
                  </div>
                  {agent.user.phone && (
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{agent.user.phone}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
          {filteredAgents("rejected").length === 0 && (
            <Card>
              <CardContent className="text-center py-8">
                <XCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No rejected agents</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="all" className="space-y-4">
          {agents.map((agent) => (
            <Card key={agent.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <Building className="h-6 w-6 text-blue-600" />
                    <div>
                      <CardTitle className="text-lg">
                        {agent.company_name}
                      </CardTitle>
                      <p className="text-sm text-gray-600">{agent.user.name}</p>
                    </div>
                  </div>
                  {getStatusBadge(agent.status)}
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{agent.user.email}</span>
                  </div>
                  {agent.user.phone && (
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{agent.user.phone}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </div>
  );
}
